import { useMutation } from '@tanstack/react-query';
import { CheckCircle2Icon, CircleDashedIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/contexts/language-context';
import { createConnectOnboardingLink } from '@/lib/api/billing';

import { StripeLogo } from './logos';

interface Props {
  businessId: string;
  chargesEnabled: boolean;
  hasStartedOnboarding: boolean;
}

// Not a form modal like the other tiles — the `stripe` payment method is
// auto-managed by the Connect webhook (see `db/migrations/017` on the
// backend), so this card only ever starts/resumes Stripe-hosted onboarding.
export function StripeConnectCard({ businessId, chargesEnabled, hasStartedOnboarding }: Props) {
  const { t } = useLanguage();

  const onboardingMutation = useMutation({
    mutationFn: () => createConnectOnboardingLink(businessId),
    meta: { errorMessage: t.stripeConnectError },
    onSuccess: (data) => {
      window.location.href = data.onboarding_url;
    },
  });

  return (
    <Card className="gap-0 py-0 overflow-hidden">
      <CardHeader className="px-5 pt-5 pb-4 border-b">
        <div className="flex items-start gap-3">
          <StripeLogo className="w-10 h-10 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm">{t.stripeConnectTitle}</CardTitle>
            <CardDescription className="text-xs mt-0.5">
              {t.stripeConnectDescription}
            </CardDescription>
          </div>
          {chargesEnabled ? (
            <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 shrink-0">
              <CheckCircle2Icon className="w-3.5 h-3.5" />
              {t.stripeConnectConnected}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground shrink-0">
              <CircleDashedIcon className="w-3.5 h-3.5" />
              {t.stripeConnectNotConnected}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-5 py-3 flex flex-col gap-2">
        <Button
          size="sm"
          variant={chargesEnabled ? 'outline' : 'default'}
          className="gap-1.5 self-start"
          disabled={onboardingMutation.isPending}
          onClick={() => onboardingMutation.mutate()}
          data-testid="stripe-connect-button"
        >
          {t.stripeConnectButton}
        </Button>
        {hasStartedOnboarding && !chargesEnabled && (
          <p className="text-xs text-muted-foreground">{t.stripeConnectPending}</p>
        )}
      </CardContent>
    </Card>
  );
}
